// =============================================================================
// MeuDriver — Admin Dashboard / Recent bookings table
// =============================================================================

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency, formatDate } from '@/lib/utils'
import type { BookingWithDetails } from '@/types/database'

function getStatusBadgeVariant(status: string) {
  switch (status) {
    case 'confirmed':
      return 'default'
    case 'completed':
      return 'secondary'
    case 'cancelled_by_student':
    case 'cancelled_by_instructor':
      return 'destructive'
    case 'no_show':
      return 'outline'
    default:
      return 'outline'
  }
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending_payment: 'Aguardando pagamento',
    confirmed: 'Confirmada',
    in_progress: 'Em andamento',
    completed: 'Concluída',
    cancelled_by_student: 'Cancelada pelo aluno',
    cancelled_by_instructor: 'Cancelada pelo instrutor',
    no_show: 'Não compareceu',
  }
  return labels[status] ?? status
}

interface Props {
  bookings: BookingWithDetails[]
  loading: boolean
}

export function AdminRecentBookings({ bookings, loading }: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Aulas Recentes</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
          </div>
        ) : bookings.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">Nenhuma aula encontrada.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-3 pr-4 font-medium">Aluno</th>
                  <th className="pb-3 pr-4 font-medium">Instrutor</th>
                  <th className="pb-3 pr-4 font-medium">Data</th>
                  <th className="pb-3 pr-4 font-medium">Valor</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 pr-4">{booking.student?.full_name ?? '—'}</td>
                    <td className="py-3 pr-4">{booking.instructor?.profile?.full_name ?? '—'}</td>
                    <td className="py-3 pr-4 text-muted-foreground">{formatDate(booking.scheduled_at, "dd/MM/yyyy 'às' HH:mm")}</td>
                    <td className="py-3 pr-4">
                      {booking.total_price != null ? formatCurrency(booking.total_price) : '—'}
                    </td>
                    <td className="py-3">
                      <Badge variant={getStatusBadgeVariant(booking.status)}>{getStatusLabel(booking.status)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
